// src/pages/UsersPage.tsx
// Main page for user management

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Filter,
  Download,
  UserCheck,
  UserX,
  Ban,
  CheckCircle,
  Eye,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import adminUserService from '../services/adminUserService';
import { User, UserFilters, UserStats } from '../types/user';
import UserStatsCards from '../components/users/UserStatsCards';
import UserFiltersPanel from '../components/users/UserFiltersPanel';
import UserTable from '../components/users/UserTable';
import Navigation from '../components/common/Navigation';
import './UsersPage.css';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: '',
    status: '',
    isVerified: null,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [showFilters, setShowFilters] = useState(false);

  // Load users
  useEffect(() => {
    loadUsers();
  }, [page, filters]);

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await adminUserService.getUsers({
        page,
        limit: 10,
        ...filters,
      });

      setUsers(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await adminUserService.getUserStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm });
    setPage(1);
  };

  const handleFilterChange = (newFilters: Partial<UserFilters>) => {
    setFilters({ ...filters, ...newFilters });
    setPage(1);
  };

  const handleRefresh = () => {
    loadUsers();
    loadStats();
  };

  const handleExport = async () => {
    try {
      await adminUserService.exportUsers(filters);
    } catch (err: any) {
      setError(err.message || 'Failed to export users');
    }
  };

  const handleViewUser = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleBulkAction = async (action: 'suspend' | 'activate') => {
    if (selectedUsers.length === 0) {
      alert('Please select users first');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to ${action} ${selectedUsers.length} user(s)?`
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      if (action === 'suspend') {
        await adminUserService.bulkSuspend(selectedUsers, 'Bulk action');
      } else {
        await adminUserService.bulkActivate(selectedUsers);
      }

      setSelectedUsers([]);
      loadUsers();
      loadStats();
    } catch (err: any) {
      setError(err.message || `Failed to ${action} users`);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u.id));
    }
  };

  return (
    <>
      <Navigation />
      <div className="users-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <Users size={32} className="header-icon" />
            <div>
              <h1>User Management</h1>
              <p>Manage passengers, drivers, and all users</p>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={handleRefresh} className="btn-icon" title="Refresh">
              <RefreshCw size={20} />
            </button>
            <button onClick={handleExport} className="btn-secondary">
              <Download size={20} />
              Export
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && <UserStatsCards stats={stats} />}

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-filter ${showFilters ? 'active' : ''}`}
          >
            <Filter size={20} />
            Filters
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <UserFiltersPanel
            filters={filters}
            onChange={handleFilterChange}
            onClose={() => setShowFilters(false)}
          />
        )}

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedUsers.length} user(s) selected</span>
            <div className="bulk-buttons">
              <button
                onClick={() => handleBulkAction('activate')}
                className="btn-success"
              >
                <UserCheck size={18} />
                Activate
              </button>
              <button
                onClick={() => handleBulkAction('suspend')}
                className="btn-danger"
              >
                <UserX size={18} />
                Suspend
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Users Table */}
        <UserTable
          users={users}
          loading={loading}
          selectedUsers={selectedUsers}
          onToggleUser={toggleUserSelection}
          onToggleAll={toggleAllUsers}
          onViewUser={handleViewUser}
        />

        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Showing {users.length} of {total} users
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="btn-icon"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="page-number">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="btn-icon"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersPage;